/**
 * #989 — Polymarket US holdings as cards for the standalone page, built from
 * the Position Tracker's own card pieces (`CardGrid`, `CardShell`,
 * `CardHeader`, `StatLine`), with the information set an international
 * position card has where the venue API gives it: the market, the side, Qty
 * and fees. Average cost, mark and P&L wait on #1199; nothing here invents
 * them. Close lives on the Tracker, where US holdings are rows of the shared
 * Open Positions list (#1226).
 *
 * Every figure comes from `presentUsPosition`. A holding G1 cannot size, or
 * can only partly account for, keeps its exact figure (or a dash) and one
 * amber word beside it; the ledger's reasons are the hover, never a list in
 * the card. See docs/260929PolymarketUsLedgerHoldings.md for what the ledger
 * basis means; the trader does not need that vocabulary to read the row.
 */
import { CardBody, CardGrid, CardHeader, CardShell, StatLine } from '@/components/cards/cardKit';
import { presentUsPosition, usPositionKey, type UsPositionView } from '@/lib/polymarketUs/portfolioModel';
import { usHoldingReason } from '@/lib/polymarketUs/reason';
import { usHoldingPartial } from '@/components/cards/positionCardModel';
import type { UsCoverage, UsPositionRow } from '@/lib/polymarketUs/types';
import { PortfolioVenueBadge } from './usVenueLabel';

const DASH = '—';

export function UsOutcomeBadge({ outcome }: { outcome: 'yes' | 'no' }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
        outcome === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}
    >
      {outcome.toUpperCase()}
    </span>
  );
}

/** One short amber word beside a partly known holding's size; nothing when
 * the holding is complete. The reasons are the hover. */
export function UsHoldingPartial({ row, className = '' }: { row: UsPositionRow; className?: string }) {
  const partial = usHoldingPartial(row);
  if (!partial) return null;
  return (
    <span className={`text-amber-400 text-[10px] font-medium light:text-amber-700 ${className}`} data-testid="us-position-partial"
      title={partial.title}>
      {partial.text}
    </span>
  );
}

/** The held size as the row shows it: the exact figure, or a dash when G1 cannot size it. */
export const usHeldText = (row: UsPositionRow, view: UsPositionView) => (row.heldQuantity === null ? DASH : view.held);
/** The recorded fee as dollars, or a dash when none is recorded. */
export const usPositionFeesText = (row: UsPositionRow, view: UsPositionView) => (row.feesUsd === null ? DASH : view.fees);
export const POSITION_FEES_TITLE = 'Recorded execution fees on this holding';

export function UsPositionCard({ row }: { row: UsPositionRow }) {
  const view = presentUsPosition(row);
  return (
    <div data-testid="us-position-card" data-position-key={view.key}>
      <CardShell ariaLabel={`${row.target.label} holding`}>
        <CardHeader
          leftBadges={
            <>
              <UsOutcomeBadge outcome={row.target.outcome} />
              <PortfolioVenueBadge venue="polymarket_us" />
            </>
          }
          // Wrap at word boundaries (the shared header's wrapTitle breaks
          // anywhere), and keep the exact slug out of its truncating subtitle.
          title={<span className="[overflow-wrap:anywhere] [word-break:normal]">{row.target.label}</span>}
          wrapTitle
        />
        <div className="text-muted-foreground -mt-1 mb-2 font-mono text-[11px] leading-tight [overflow-wrap:anywhere]" data-testid="us-position-slug">
          {row.target.marketSlug}
        </div>
        <CardBody>
          <StatLine label="Qty" valueClass="min-w-0 max-w-[75%] text-right [overflow-wrap:anywhere]"
            value={<><UsHoldingPartial row={row} className="mr-1.5 font-sans" /><span data-testid="us-position-held">{usHeldText(row, view)}</span></>} />
          <StatLine label="Fees" title={POSITION_FEES_TITLE} value={<span data-testid="us-position-fees">{usPositionFeesText(row, view)}</span>} />
        </CardBody>
      </CardShell>
    </div>
  );
}

/** What shows instead of holdings when the read has none to lay out:
 * unavailable (never shown as "none"), or none open. Null otherwise. */
export function usHoldingsInstead(positions: readonly UsPositionRow[], holdings: UsCoverage | null) {
  if (holdings?.state === 'unavailable' && positions.length === 0) {
    return (
      <p className="text-muted-foreground px-4 py-3 text-xs" data-testid="us-holdings-unavailable"
        title={usHoldingReason(holdings.reason)}>
        Holdings unavailable
      </p>
    );
  }
  if (positions.length === 0) {
    return <p className="text-muted-foreground px-4 py-3 text-xs">No open positions</p>;
  }
  return null;
}

/** The Position Tracker offers Close on each US holding (#1226); here it is
 * said once rather than shown as a control that could only fail (no dead
 * controls, docs/260926PolymarketUsFrontend.md). */
export function UsCloseNote() {
  return (
    <p className="text-muted-foreground mb-2 text-xs" data-testid="us-close-unavailable">
      Close isn’t offered for Polymarket US holdings on this page. Use Close in the Position Tracker.
    </p>
  );
}

export function UsPositionCards({ positions, holdings = null }: {
  positions: readonly UsPositionRow[];
  /** Holdings coverage, when known. Unavailable holdings are never shown as "none". */
  holdings?: UsCoverage | null;
}) {
  const instead = usHoldingsInstead(positions, holdings);
  if (instead) return instead;
  return (
    <div className="px-4 py-3">
      <UsCloseNote />
      <CardGrid>
        {positions.map((row) => (
          <UsPositionCard key={usPositionKey(row)} row={row} />
        ))}
      </CardGrid>
    </div>
  );
}
